import { useEffect, useMemo, useState } from 'react';
import { api, type ProfitDirectRow } from '../lib/api';
import { ErrorBox, money } from '../components/DataTable';
import { CalcNote } from '../components/CalcNote';
import { useProfitMonths } from './profitMonths';

/**
 * 경영 분석 › 관리기사A 차량 — 시트 `직영차량분석SYS` 「직영통합분석」 대체(10-27). 서버 `getProfitDirect`(`functions/src/profitDirect.ts`).
 * 차량마다 매출 − 추가운행수당 − 기본급 + 재무지급 − 재무공제 − 4대보험(회사) − 할부금(또는 감가상각) = 수익.
 * 굳힌 달은 기록(월 수익 굳히기와 같이 굳는다) · 안 굳힌 달은 지금 원장으로 센 값.
 * 상단은 한 줄 · 계산 설명은 「ⓘ 계산 기준」 · 머리글 누르면 오름 → 내림 → 기본(10-27 way 캡처).
 */

type SortKey = keyof ProfitDirectRow;
type Sort = { key: SortKey; dir: 1 | -1 };
const DEFAULT_SORT: Sort = { key: 'profit_dep', dir: 1 };
const TEXT_KEYS = new Set<SortKey>(['driver_name', 'car_no', 'corp', 'custs']);
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const n = (v: string | null | undefined) => Number(v ?? 0);
const COLS: [keyof ProfitDirectRow, string, string?][] = [
  ['sales', '매출'], ['commute_sales', '통근'], ['general_sales', '일반'], ['extra_pay', '추가운행수당'], ['base_pay', '기본급'],
  ['drv_deduct', '기사공제', '월정기 공제(4대·소득세 등) — 수익 계산엔 안 들어간다'], ['ins_company', '4대보험(회사)', '국민연금·건강·장기요양 + 고용 ×1.15/0.9 + 산재'],
  ['fin_pay', '재무지급'], ['fin_deduct', '재무공제'], ['inst', '할부금'], ['dep', '감가상각'],
];
const LEFT: [SortKey, string][] = [['driver_name', '기사'], ['car_no', '차량'], ['corp', '법인'], ['car_year', '연식']];
const SOURCE: Record<string, string> = { sheet: '시트에서 옮긴 기록', web: '굳힌 기록', live: '지금 원장으로 센 값(굳히기 전)' };

export default function ProfitDirect() {
  const [error, setError] = useState<string | null>(null);
  const [months, mon, setMon] = useProfitMonths(setError);
  const [data, setData] = useState<{ source: string; cutAt: string | null; rows: ProfitDirectRow[] } | null>(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [minusOnly, setMinusOnly] = useState(false);

  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setData(null); setError(null);
    api.getProfitDirect({ mon }).then((r) => { if (!cancelled) setData(r); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [mon]);

  const sum = useMemo(() => {
    const rows = data?.rows ?? [];
    const t = (k: keyof ProfitDirectRow) => rows.reduce((s, r) => s + n(r[k] as string), 0);
    return {
      cars: rows.length, sales: t('sales'), inst: t('inst'), dep: t('dep'), pi: t('profit_inst'), pd: t('profit_dep'),
      minusI: rows.filter((r) => n(r.profit_inst) < 0).length, minusD: rows.filter((r) => n(r.profit_dep) < 0).length,
    };
  }, [data]);
  const shown = useMemo(() => {
    const w = q.trim().replace(/\s/g, '');
    const rows = (data?.rows ?? []).filter((r) => (!minusOnly || n(r.profit_dep) < 0)
      && (!w || r.car_no.includes(w) || (r.driver_name ?? '').includes(w) || (r.corp ?? '').includes(w) || r.custs.replace(/\s/g, '').includes(w)));
    const { key, dir } = sort;
    return TEXT_KEYS.has(key)
      ? rows.sort((a, b) => dir * String(a[key] ?? '').localeCompare(String(b[key] ?? ''), 'ko'))
      : rows.sort((a, b) => dir * (n(a[key] as string) - n(b[key] as string)));
  }, [data, q, sort, minusOnly]);

  const onSort = (key: SortKey) => setSort((s) => (s.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : DEFAULT_SORT));
  const th = (key: SortKey, label: string, right: boolean, title?: string) => (
    <th key={key} className={`cursor-pointer select-none whitespace-nowrap px-2 py-1.5 hover:text-ink ${right ? 'text-right' : 'text-left'}`}
        title={title ?? '누르면 오름 → 내림 → 기본 순'} onClick={() => onSort(key)}>
      {label}<span className="inline-block w-3 text-center">{sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}</span>
    </th>
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <select className="field w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="정산월">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className="field w-44" placeholder="차량·기사·법인·거래처" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={minusOnly} onChange={(e) => setMinusOnly(e.target.checked)} />수익(−)만</label>
        {data && (
          <span className="flex flex-wrap gap-x-3 text-xs">
            <span>{sum.cars}대</span>
            <span>매출 <b>{money(sum.sales)}</b></span>
            <span className="text-ink-soft">할부금 {money(sum.inst)} · 감가상각 {money(sum.dep)}</span>
            <span>수익(할부금) <b className={sum.pi < 0 ? 'text-red-700' : ''}>{money(Math.round(sum.pi))}</b> · (−) {sum.minusI}대</span>
            <span>수익(감가상각) <b className={sum.pd < 0 ? 'text-red-700' : ''}>{money(Math.round(sum.pd))}</b> · (−) {sum.minusD}대</span>
          </span>
        )}
        <CalcNote>
          <p>대상 = 그 달 명세 중 차주가 「동영」·「더모아」인 차량(관리기사A) · 매출 등 금액은 차량번호에 경기·서울·78우 가 든 차만 셉니다(옛 시트 그대로).</p>
          <p>수익 = 매출 − 추가운행수당 − 기본급 + 재무지급 − 재무공제 − 4대보험(회사) − 할부금(또는 감가상각). 기사공제는 참고 칸입니다.</p>
          <p>4대보험(회사)의 산재 = 그 차 첫 기사의 차인지급액 × 법인 요율 — 요율이 없는 법인(동영(이천) 등)은 수익을 0 으로 둡니다.</p>
          <p>감가상각 = (취득가 − 취득가 10%) ÷ 108개월(수소차 기준가 205,260,000) · 등록월부터 108개월.</p>
        </CalcNote>
        {data && <span className="ml-auto text-xs text-ink-soft">{SOURCE[data.source] ?? data.source}{data.cutAt ? ` · ${data.cutAt.slice(0, 16)}` : ''}</span>}
      </div>
      {error && <ErrorBox message={error} />}
      {!data && !error && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {data && (
        <div className="card max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="w-full min-w-[88rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                {LEFT.map(([k, h]) => th(k, h, false))}
                {COLS.map(([k, h, t]) => th(k, h, true, t))}
                {th('profit_inst', '수익(할부금)', true)}{th('profit_dep', '수익(감가상각)', true)}
                {th('custs', '거래처', false)}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.car_no} className="border-b border-line/60 hover:bg-surface-soft">
                  <td className="whitespace-nowrap px-2 py-1">{r.driver_name ?? '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1">{r.car_no}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-ink-soft">{r.corp ?? '—'}</td>
                  <td className="px-2 py-1 text-ink-soft">{r.car_year ?? '—'}</td>
                  {COLS.map(([k]) => <td key={k} className={`px-2 py-1 text-right ${k === 'drv_deduct' ? 'text-ink-faint' : ''}`}>{money(Math.round(n(r[k] as string)))}</td>)}
                  {(['profit_inst', 'profit_dep'] as const).map((k) => (
                    <td key={k} className={`px-2 py-1 text-right font-medium ${n(r[k]) < 0 ? 'text-red-700' : ''}`}>{money(Math.round(n(r[k])))}</td>
                  ))}
                  <td className="max-w-[24rem] truncate px-2 py-1 text-ink-soft" title={r.custs.split('#').join(', ')}>{r.custs.split('#').join(', ') || '—'}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={COLS.length + 7} className="py-6 text-center text-ink-soft">해당하는 차량이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
